import * as React from "react";
import {
  Link,
  NavLink,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import { useEffect, useState, FunctionComponent, FormEvent } from "react";
import { Switch } from "@headlessui/react";
import { LayoutRoutes } from "../routes/index.tsx";
import { ChildProps } from "../utils/types/react.types";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store/store.ts";
import { toggleDarkMode } from "../store/slices/session.slice.ts";

export const MainLayout: FunctionComponent<ChildProps> = ({ children }) => {
  const sessionData = useSelector((state: RootState) => state.session);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState<"movie" | "tv">("movie");

  useEffect(() => {
    sessionStorage.setItem("darkmode", JSON.stringify(sessionData?.darkmode));
  }, [sessionData]);

  const onSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = searchTerm.trim();
    if (!q) return;
    navigate(
      `/search?type=${searchType}&search-term=${encodeURIComponent(q)}`,
    );
    setSearchTerm("");
  };

  const theme = sessionData?.darkmode ? "dark" : "light";

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    isActive ? "active nav-link" : "nav-link";

  return (
    <div className="app-shell" data-theme={theme}>
      <header className="main-header">
        <div className="container header-row">
          <div className="logo header-brand">
            <Link to="/">Flixtor</Link>
          </div>

          <nav className="header-nav" aria-label="Primary">
            <ul>
              <li>
                <NavLink className={navLinkClass} to="/" end>
                  Home
                </NavLink>
              </li>
              <li>
                <NavLink className={navLinkClass} to="/movies">
                  Movies
                </NavLink>
              </li>
              <li>
                <NavLink className={navLinkClass} to="/shows">
                  TV Shows
                </NavLink>
              </li>
              <li>
                <NavLink className={navLinkClass} to="/discover">
                  Discover
                </NavLink>
              </li>
              <li>
                <NavLink className={navLinkClass} to="/people">
                  People
                </NavLink>
              </li>
              <li>
                <details className="nav-details">
                  <summary>Genres</summary>
                  <div className="nav-details-panel">
                    <Link to="/genres/movie">Movie genres</Link>
                    <Link to="/genres/tv">TV genres</Link>
                  </div>
                </details>
              </li>
            </ul>
          </nav>

          <form
            className="header-search"
            onSubmit={onSearchSubmit}
            role="search"
            aria-label="Search movies and TV"
          >
            <fieldset className="header-search-fieldset">
              <legend className="sr-only">
                Search titles — choose movies or TV, then type a query
              </legend>
              <div className="header-search-unified">
                <div
                  className="header-search-type"
                  role="radiogroup"
                  aria-label="Search in"
                >
                  <label>
                    <input
                      type="radio"
                      name="header-search-type"
                      checked={searchType === "movie"}
                      onChange={() => setSearchType("movie")}
                    />
                    Movies
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="header-search-type"
                      checked={searchType === "tv"}
                      onChange={() => setSearchType("tv")}
                    />
                    TV
                  </label>
                </div>
                <div className="header-search-row">
                  <input
                    type="search"
                    name="q"
                    placeholder="Search titles..."
                    autoComplete="off"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    aria-label="Search query"
                  />
                  <button type="submit" aria-label="Submit search">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width={18}
                      height={18}
                      aria-hidden
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.25}
                      strokeLinecap="round"
                    >
                      <circle cx="10.5" cy="10.5" r="6.5" />
                      <path d="M16 16l4.5 4.5" />
                    </svg>
                  </button>
                </div>
              </div>
            </fieldset>
          </form>

          <div className="header-theme">
            <Switch
              checked={sessionData?.darkmode}
              onChange={() => dispatch(toggleDarkMode([]))}
              className="theme-switch"
            >
              <span className="sr-only">Toggle dark theme</span>
              <span className="theme-switch-knob" aria-hidden />
            </Switch>
          </div>
        </div>
      </header>

      <main className="app-main">{children}</main>

      <footer className="main-footer">
        <div className="container">
          <div className="logo">
            <span>Flixtor</span>
          </div>
          <nav className="footer-links" aria-label="Footer">
            <Link to="/privacy">Privacy</Link>
          </nav>
          <div className="social-links">
            <a
              href="https://www.themoviedb.org/"
              target="_blank"
              rel="noreferrer"
              aria-label="The Movie Database"
            >
              <i className="fas fa-film" />
            </a>
            <a
              href="https://www.themoviedb.org/documentation/api"
              target="_blank"
              rel="noreferrer"
              aria-label="TMDB API documentation"
            >
              <i className="fas fa-book" />
            </a>
          </div>
        </div>
      </footer>

      <div className="spinner" />
    </div>
  );
};

const PageContainer: React.FC<ChildProps> = ({ children }) => {
  return (
    <Routes>
      <Route
        path="/*"
        element={
          <MainLayout>
            <LayoutRoutes />
          </MainLayout>
        }
      />
    </Routes>
  );
};

export default PageContainer;
